import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import PrivatePreviewPage from './pages/PrivatePreviewPage';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { SeasonProvider } from './context/SeasonContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FloatingFavorites from './components/FloatingFavorites';
import CookieConsent from './components/CookieConsent';
import AdminSamplePreviewToggle from './components/AdminSamplePreviewToggle';
import PcsBuddyAssistPrompt from './components/PcsBuddyAssistPrompt';
import { ViewAsProvider } from './context/ViewAsContext';
import RoleRouteGuard from './components/RoleRouteGuard';
import VerificationRouteGuard from './components/VerificationRouteGuard';
import ScrollToTop from './components/ScrollToTop';
import PlausibleScript from './components/PlausibleScript';
import { ExperienceProvider } from './experience/ExperienceProvider';
import FlowBar from './experience/nav/FlowBar';
import SkipToMain from './experience/nav/SkipToMain';

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyEmailPendingPage from './pages/VerifyEmailPendingPage';
import ListingsPage from './pages/ListingsPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import LocationsPage from './pages/LocationsPage';
import ConciergePage from './pages/ConciergePage';
import LocationMatchPage from './pages/LocationMatchPage';
import LocalFavoritesPage from './pages/LocalFavoritesPage';
import ClientDashboard from './pages/ClientDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import OwnerListingsPage from './pages/OwnerListingsPage';
import ListingEditor from './pages/ListingEditor';
import AdminPage from './pages/AdminPage';
import MarketingStudioPage from './pages/MarketingStudioPage';
import PricingPage from './pages/PricingPage';
import SettingsPage from './pages/SettingsPage';
import FavoritesPage from './pages/FavoritesPage';
import NotificationsPage from './pages/NotificationsPage';
import AlertsPage from './pages/AlertsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InsurancePage from './pages/InsurancePage';
import ComparePage from './pages/ComparePage';
import NeighborhoodGuidePage from './pages/NeighborhoodGuidePage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import RentalCarsPage from './pages/RentalCarsPage';
import PCSChecklistPage from './pages/PCSChecklistPage';
import LifeJourneyPage from './pages/LifeJourneyPage';
import FAQPage from './pages/FAQPage';
import MyBookingsPage from './pages/MyBookingsPage';
import ImprintPage from './pages/ImprintPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import PhrasesPage from './pages/PhrasesPage';
import LanguagePhrasesPage from './pages/LanguagePhrasesPage';
import OwnerPlansPage from './pages/OwnerPlansPage';
import TLACalculatorPage from './pages/TLACalculatorPage';

function Layout({ children }) {
  const location = useLocation();
  const noLayoutPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/verify-email-pending'];
  const showLayout = !noLayoutPaths.includes(location.pathname);
  const showFooter = showLayout;

  return (
    <div className="min-h-screen flex flex-col bg-ivory">
      <SkipToMain />
      {showLayout && <Header />}
      {showLayout && <FlowBar />}
      <main id="main-content" tabIndex={-1} className={`flex-1 outline-none${showLayout ? ' pt-12' : ''}`}>{children}</main>
      {showFooter && <Footer />}
      {showLayout && <FloatingFavorites />}
      {showLayout && <PcsBuddyAssistPrompt />}
      {showLayout && location.pathname.startsWith('/admin') && <AdminSamplePreviewToggle />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

const PRIVATE_MODE = process.env.REACT_APP_PRIVATE_MODE === 'true';
const PRIVATE_MODE_ALLOWED_PATHS = ['/login', '/forgot-password', '/reset-password', '/verify-email', '/verify-email-pending'];

function PrivateModeGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (!PRIVATE_MODE) return children;
  if (PRIVATE_MODE_ALLOWED_PATHS.includes(location.pathname)) return children;

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-signal-blue animate-spin" />
      </div>
    );
  }

  // Any authenticated user may access the site in private mode.
  // Private mode is for blocking anonymous visitors only (staging/preview).
  if (user) return children;

  return <PrivatePreviewPage isLoggedIn={!!user} />;
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <SeasonProvider>
          <BrowserRouter>
            <PlausibleScript />
            <ScrollToTop />
            <ViewAsProvider>
            <ExperienceProvider>
            <RoleRouteGuard />
            <VerificationRouteGuard />
            <PrivateModeGate>
              <Layout>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                <Route path="/" element={<HomePage />} />
                <Route path="/listings" element={<ListingsPage />} />
                <Route path="/listings/:listingId" element={<PropertyDetailPage />} />
                <Route path="/locations" element={<LocationsPage />} />
                <Route path="/locations/:locationId" element={<LocationsPage />} />
                <Route path="/concierge" element={<ConciergePage />} />
                <Route path="/location-match" element={<LocationMatchPage />} />
                <Route path="/local-favorites" element={<LocalFavoritesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/neighborhood/:locationId" element={<NeighborhoodGuidePage />} />

                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/dashboard" element={<ClientDashboard />} />
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/bookings" element={<MyBookingsPage />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/insurance" element={<InsurancePage />} />
                <Route path="/rental-cars" element={<RentalCarsPage />} />
                <Route path="/pcs-checklist" element={<PCSChecklistPage />} />
                <Route path="/journey" element={<LifeJourneyPage />} />
                <Route path="/tla-calculator" element={<TLACalculatorPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/imprint" element={<ImprintPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/phrases" element={<PhrasesPage />} />
                <Route path="/phrases/:languageCode" element={<LanguagePhrasesPage />} />

                <Route path="/owner/dashboard" element={<OwnerDashboard />} />
                <Route path="/owner/listings" element={<OwnerListingsPage />} />
                <Route path="/owner/plans" element={<OwnerPlansPage />} />
                <Route path="/owner/listings/new" element={<ListingEditor />} />
                <Route path="/owner/listings/:listingId/edit" element={<ListingEditor />} />

                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/marketing" element={<MarketingStudioPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <CookieConsent />
            </Layout>
            </PrivateModeGate>
            </ExperienceProvider>
            </ViewAsProvider>
          </BrowserRouter>
        </SeasonProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
